import { faTrash, faUserSecret } from '@fortawesome/free-solid-svg-icons'
import { faFileUser, faXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { useApplicant } from '~/api/queries/applicant'
import { TextInput } from '~/components/Form/Input/TextInput'
import { ButtonGroup } from '~/components/Global/Blocks/ButtonGroup/ButtonGroup'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { Dropdown as FloatingDropdown } from '~/components/Global/Interactions/Dropdown/Dropdown'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import {
  useApplicantAttachmentContent,
  useApplicantAttachments
} from '~/domains/applicants/queries/applicantAttachments'
import { useApplicantFormations } from '~/domains/applicants/queries/applicantFormations'
import { AttachmentsViewer } from '~/domains/attachments/components/AttachmentsViewer'
import {
  attachmentsViewer,
  attachmentsViewerEditorContent,
  attachmentsViewerEditorFilename,
  attachmentsViewerEditorToolbar,
  attachmentsViewerToolbarItem,
  attachmentsViewerToolbarItemActionnable,
  templateDropdownItem,
  templateDropdownWrapper
} from '~/domains/attachments/components/AttachmentsViewer.css'
import {
  ATTACHMENT_TYPE_RESUME,
  type AttachmentType
} from '~/domains/attachments/types'
import { detectAttachmentFormat } from '~/domains/attachments/utils/formatDetector'
import { useConfig } from '~/domains/configuration/queries/config'
import type { DocumentEditorRef } from '~/domains/documents/components/DocumentEditor'
import { useDeleteGeneratedAttachmentMutation } from '~/domains/documents/mutations/deleteGeneratedAttachment'
import { useSaveGeneratedResumeMutation } from '~/domains/documents/mutations/saveGeneratedResume'
import { useDocumentTemplates } from '~/domains/documents/queries/useDocumentTemplates'
import {
  type ApplicantResumeContext,
  resolveResumeInputs
} from '~/domains/documents/resumePlaceholderResolver'
import { themeClass } from '~/styles/theme.css'
import { confirm, toast } from '~/utils'

const LazyResumeDocumentDesigner = lazy(() =>
  import('~/domains/documents/components/ResumeDocumentDesigner').then((m) => ({
    default: m.ResumeDocumentDesigner
  }))
)

export const ApplicantAttachmentsViewer = ({
  applicantId,
  defaultScale,
  canAnonymize,
  showPinButton
}: {
  applicantId: number
  defaultScale?: number
  canAnonymize?: boolean
  showPinButton?: boolean
}) => {
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const queryClient = useQueryClient()

  const { data: applicant } = useApplicant({ id: applicantId })
  const { data: formations } = useApplicantFormations(applicantId)
  const { data: organizationSlug } = useConfig('organization.slug')

  const resumeContext: ApplicantResumeContext | undefined = applicant
    ? {
        firstname: applicant.firstname,
        lastname: applicant.lastname,
        email: applicant.email,
        phoneMobile: applicant.phoneMobile,
        phoneSecondary: applicant.phoneSecondary,
        comment: applicant.comment,
        address: applicant.address,
        qualifications: applicant.qualifications,
        experiences: applicant.experiences,
        formations,
        languages: applicant.languages,
        softwares: applicant.softwares,
        mobilities: applicant.mobilities
      }
    : undefined

  // CV generation state
  const [isEditorMode, setIsEditorMode] = useState(false)
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [editorInputs, setEditorInputs] = useState<Record<string, string>[]>([])
  const [editorFilename, setEditorFilename] = useState('')
  const editorRef = useRef<DocumentEditorRef>(null)
  const editorFilenameRef = useRef(editorFilename)
  editorFilenameRef.current = editorFilename
  const generateButtonRef = useRef<HTMLSpanElement>(null)
  const replaceAttachmentIdRef = useRef<number | null>(null)
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    null
  )

  const canGenerateResume = !!resumeContext

  const { data: resumeTemplatesData } = useDocumentTemplates(
    'resume',
    undefined,
    {
      enabled: canGenerateResume
    }
  )
  const resumeTemplates = resumeTemplatesData?.['hydra:member'] ?? []

  const saveResumeMutation = useSaveGeneratedResumeMutation(applicantId)
  const deleteAttachmentMutation =
    useDeleteGeneratedAttachmentMutation(applicantId)

  const selectedTemplate =
    resumeTemplates.find((t) => t.id === selectedTemplateId) ?? null

  useEffect(() => {
    if (window.applicant) {
      window.applicant.refreshAttachments = (id: number) => {
        void queryClient.invalidateQueries({
          queryKey: ['applicants', id, 'attachments']
        })
      }
    }
    return () => {
      if (window.applicant) {
        delete window.applicant.refreshAttachments
      }
    }
  }, [queryClient])

  const { data: attachmentsData, isLoading } = useApplicantAttachments({
    applicantId
  })

  const attachmentsMetadata: AttachmentType[] = useMemo(
    () =>
      (attachmentsData ?? []).map((attachment) => ({
        id: attachment.id,
        name: attachment.object.filename ?? '',
        formatInfo: detectAttachmentFormat(attachment.object.filename ?? ''),
        type: attachment.type?.name ?? 'document',
        system: attachment.type?.system,
        generated: attachment.generated,
        isDefaultResume: attachment.defaultResume
      })),
    [attachmentsData]
  )

  const effectiveSelectedId = selectedId ?? attachmentsMetadata[0]?.id ?? null

  const { data: contentData } = useApplicantAttachmentContent({
    attachmentId: effectiveSelectedId ?? 0,
    enabled: effectiveSelectedId !== null
  })

  const attachments: AttachmentType[] = useMemo(
    () =>
      attachmentsMetadata.map((attachment) => {
        if (
          attachment.id !== effectiveSelectedId ||
          contentData === undefined
        ) {
          return attachment
        }
        return {
          ...attachment,
          content: contentData.object.base64Content,
          mimeType: contentData.object.mime,
          url: contentData.object.url
        }
      }),
    [attachmentsMetadata, effectiveSelectedId, contentData]
  )

  const selectedAttachmentData =
    attachments.find((a) => a.id === effectiveSelectedId) ?? null

  const handleGenerateClick = () => {
    if (resumeTemplates.length === 0) return
    if (resumeTemplates.length === 1 && resumeTemplates[0]) {
      startEditorWithTemplate(resumeTemplates[0].id)
    } else {
      setShowTemplateModal(true)
    }
  }

  const startEditorWithTemplate = (templateId: number) => {
    const template = resumeTemplates.find((t) => t.id === templateId)
    if (!template || !resumeContext) return
    setSelectedTemplateId(templateId)
    const inputs = resolveResumeInputs(template.templateData, resumeContext)
    setEditorInputs(inputs)
    setEditorFilename(`${organizationSlug ?? 'CV'}_${applicantId}.pdf`)
    setIsEditorMode(true)
    setShowTemplateModal(false)
  }

  const handleCancelEditor = async () => {
    if (
      !(await confirm(
        'Annuler la génération ?',
        'Les modifications non sauvegardées seront perdues.',
        'Confirmer'
      ))
    )
      return
    replaceAttachmentIdRef.current = null
    setIsEditorMode(false)
    setSelectedTemplateId(null)
    setEditorInputs([])
  }

  const handleSaveResume = async () => {
    if (!editorRef.current) return

    const filename =
      editorFilename.trim() || `${organizationSlug ?? 'CV'}_${applicantId}.pdf`

    const existingGeneratedFile = attachments.find(
      (a) => a.name === filename && a.generated
    )
    if (existingGeneratedFile) {
      if (
        !(await confirm(
          'Fichier existant',
          `Un fichier nommé "${filename}" existe déjà. Voulez-vous l'écraser ?`,
          'Écraser'
        ))
      )
        return
      replaceAttachmentIdRef.current = existingGeneratedFile.id
    }

    await editorRef.current.exportPdf()
  }

  const handleExportPdf = async (blob: Blob) => {
    const filename =
      editorFilenameRef.current.trim() ||
      `${organizationSlug ?? 'CV'}_${applicantId}.pdf`

    try {
      await saveResumeMutation.mutateAsync({ blob, filename })
      toast(
        'CV sauvegardé',
        'Le CV a été enregistré dans les documents.',
        ToastType.Success
      )
    } catch {
      toast(
        'Erreur',
        'Une erreur est survenue lors de la sauvegarde.',
        ToastType.Danger
      )
      replaceAttachmentIdRef.current = null
      return
    }

    if (replaceAttachmentIdRef.current !== null) {
      const idToDelete = replaceAttachmentIdRef.current
      replaceAttachmentIdRef.current = null
      try {
        await deleteAttachmentMutation.mutateAsync(idToDelete)
      } catch {
        toast(
          'Attention',
          "Le nouveau CV a été sauvegardé mais l'ancien n'a pas pu être supprimé. Vous pouvez le supprimer manuellement.",
          ToastType.Warning
        )
      }
    }

    window.location.reload()
  }

  const handleDeleteGenerated = async (attachmentId: number) => {
    if (
      !(await confirm(
        'Supprimer ce fichier généré ?',
        'Cette action est irréversible.',
        'Confirmer la suppression'
      ))
    )
      return
    await deleteAttachmentMutation.mutateAsync(attachmentId, {
      onSuccess: () => {
        toast(
          'Fichier supprimé',
          'Le fichier généré a été supprimé.',
          ToastType.Success
        )
        setSelectedId(null)
        window.location.reload()
      },
      onError: () => {
        toast(
          'Erreur',
          'Une erreur est survenue lors de la suppression.',
          ToastType.Danger
        )
      }
    })
  }

  const toolbarActions = selectedAttachmentData ? (
    <>
      {canAnonymize &&
        selectedAttachmentData.system === ATTACHMENT_TYPE_RESUME && (
          <Tooltip text="Anonymiser le CV" direction="top">
            <span
              className={clsx(
                attachmentsViewerToolbarItem,
                attachmentsViewerToolbarItemActionnable
              )}
              onClick={(e) => {
                e.preventDefault()
                window.location.href = `/admin/anonymize/index/${selectedAttachmentData.id}`
              }}
            >
              <FontAwesomeIcon icon={faUserSecret} />
            </span>
          </Tooltip>
        )}
      {canGenerateResume &&
        selectedAttachmentData.system === ATTACHMENT_TYPE_RESUME &&
        resumeTemplates.length > 0 && (
          <Tooltip text="Générer un CV" direction="top">
            <span
              ref={generateButtonRef}
              className={clsx(
                attachmentsViewerToolbarItem,
                attachmentsViewerToolbarItemActionnable
              )}
              onClick={(e) => {
                e.preventDefault()
                handleGenerateClick()
              }}
            >
              <FontAwesomeIcon icon={faFileUser} />
            </span>
          </Tooltip>
        )}
      {canGenerateResume && selectedAttachmentData.generated && (
        <Tooltip text="Supprimer le fichier généré" direction="top">
          <span
            className={clsx(
              attachmentsViewerToolbarItem,
              attachmentsViewerToolbarItemActionnable
            )}
            onClick={(e) => {
              e.preventDefault()
              handleDeleteGenerated(selectedAttachmentData.id)
            }}
          >
            <FontAwesomeIcon icon={faTrash} />
          </span>
        </Tooltip>
      )}
    </>
  ) : null

  if (isEditorMode && selectedTemplate) {
    return (
      <div className={clsx(attachmentsViewer, themeClass)}>
        <div
          className={attachmentsViewerEditorToolbar}
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
          }}
        >
          <div className={attachmentsViewerEditorFilename}>
            <TextInput
              standalone
              small
              name="filename"
              value={editorFilename}
              placeholder="Nom du fichier..."
              onChange={(e) => setEditorFilename(e.currentTarget.value)}
            />
          </div>
          <ButtonGroup>
            <Button
              variation={ButtonVariation.Secondary}
              onClick={handleCancelEditor}
            >
              <FontAwesomeIcon icon={faXmark} />
              Annuler
            </Button>
            <Button
              variation={ButtonVariation.Primary}
              onClick={handleSaveResume}
              disabled={saveResumeMutation.isPending}
            >
              {saveResumeMutation.isPending ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </ButtonGroup>
        </div>
        <div
          className={attachmentsViewerEditorContent}
          onChangeCapture={(e) => e.stopPropagation()}
        >
          <Suspense fallback={<Shimmer height={600} />}>
            <LazyResumeDocumentDesigner
              ref={editorRef}
              template={selectedTemplate.templateData}
              inputs={editorInputs}
              onExportPdf={handleExportPdf}
            />
          </Suspense>
        </div>
      </div>
    )
  }

  return (
    <>
      <AttachmentsViewer
        attachments={attachments}
        isLoading={isLoading}
        defaultAttachmentId={attachmentsMetadata[0]?.id}
        defaultScale={defaultScale}
        showPinButton={showPinButton}
        groupByType
        toolbarActions={toolbarActions}
        onAttachmentChange={setSelectedId}
      />
      <FloatingDropdown
        isOpen={showTemplateModal}
        onclose={() => setShowTemplateModal(false)}
        dropdownRef={generateButtonRef}
        placement="bottom-end"
        className={templateDropdownWrapper}
      >
        {resumeTemplates.map((template) => (
          <div
            key={template.id}
            className={templateDropdownItem}
            onClick={(e) => {
              e.stopPropagation()
              startEditorWithTemplate(template.id)
            }}
          >
            {template.name}
          </div>
        ))}
      </FloatingDropdown>
    </>
  )
}
